import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NotebookType, NotebookSidebarType, PreferenceResource, NotebookFacade } from '@mode/notebooks/contract';
import { FeatureFlag, FeatureFlagsFacade, OrgTypes } from '@mode/shared/contract-common';
import { getInitialFlagValue } from '@mode/shared/util-js';
import { filter, map, skip, distinctUntilChanged, takeUntil, take } from 'rxjs/operators';
import { AnalyticsService, AnalyticsEvent, RunMethodType } from '@mode/shared/util-segment';
import { Subject } from 'rxjs';

@Component({
  selector: 'mode-notebook-sidebar',
  templateUrl: './notebook-sidebar.component.html',
  styleUrl: './notebook-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class NotebookSidebarComponent implements OnInit {
  @ViewChild('helpContainer') helpContainer!: ElementRef;
  private destroyed$ = new Subject<void>();
  notebookToken!: string;
  notebookType!: NotebookType;
  isPythonEdge!: boolean;
  activeTab!: NotebookSidebarType;
  itemExpandedOrCollapsed!: { [key: string]: boolean };
  isRegisterSecureCredentialsEnabled = getInitialFlagValue(FeatureFlag.RegisterSecureCredentialsNotebooks);
  isNotebookLibraryCleanslateEnabled$;

  LANGUAGES = [
    { type: NotebookType.Python3, name: 'Python 3' },
    { type: NotebookType.R, name: 'R' },
  ];

  constructor(
    private analytics: AnalyticsService,
    private notebookFacade: NotebookFacade,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.isNotebookLibraryCleanslateEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.NotebookLibraryCleanslate
    );
  }

  ngOnInit() {
    this.notebookFacade.notebookType$.pipe(takeUntil(this.destroyed$)).subscribe((type) => {
      this.notebookType = type;
      this.changeIsPythonEdge();
    });
    this.notebookFacade.notebookToken$.pipe(takeUntil(this.destroyed$)).subscribe((token) => {
      this.notebookToken = token;
    });

    this.notebookFacade.accountPreference$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ notebook_sidebar: { tab, items } }) => {
        this.activeTab = tab;
        this.itemExpandedOrCollapsed = items;
        this.track(AnalyticsEvent.Name.NotebookSidebarContentImpression, 'impression', { tab });
      });
    this.notebookFacade.accountPreference$
      .pipe(
        map(({ notebook_sidebar: { tab } }) => tab),
        filter(Boolean),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.destroyed$)
      )
      .subscribe((tab) => {
        this.track(AnalyticsEvent.Name.NotebookSidebarTabChange, 'click', { tab });
      });

    this.notebookFacade.accountContextPlanCode$.pipe(takeUntil(this.destroyed$)).subscribe((planCode) => {
      if (planCode === OrgTypes.PlanCode.Plus) {
        this.LANGUAGES.push({ type: NotebookType.Python3Edge, name: 'Python 3 Edge' });
      }
    });

    this.isNotebookLibraryCleanslateEnabled$.pipe(take(1)).subscribe((isEnabled) => {
      isEnabled && this.LANGUAGES.push({ type: NotebookType.Python3CleanSlate, name: 'Python 3 Cleanslate' });
    });

    if (!this.isRegisterSecureCredentialsEnabled && this.activeTab === NotebookSidebarType.Secrets) {
      this.goToTab(NotebookSidebarType.Resources);
    }
  }

  changeNotebookType(notebookType: NotebookType) {
    this.notebookType = notebookType;
    this.changeIsPythonEdge();
    if (this.notebookType && this.notebookToken) {
      this.notebookFacade.updateNotebookType(this.notebookToken, this.notebookType);
      this.analytics.run(RunMethodType.Track, AnalyticsEvent.Name.NotebookEnvironmentSelectorClicked);
    }
    if (this.notebookType === NotebookType.R && this.activeTab === NotebookSidebarType.Secrets) {
      this.goToTab(NotebookSidebarType.Resources);
    }
  }
  changeIsPythonEdge() {
    this.isPythonEdge = this.notebookType === NotebookType.Python3Edge;
  }

  goToTab(tab: NotebookSidebarType | any) {
    this.update({ tab });
  }

  toggle(item: string) {
    const wasOpen = this.itemExpandedOrCollapsed[item];
    this.update({ items: { [item]: !wasOpen } });
    this.track(AnalyticsEvent.Name.NotebookSidebarExpandableTextToggled, 'click', {
      item,
      was: wasOpen ? 'open' : 'closed',
    });
  }

  goToSupportedLibraries() {
    this.update({ tab: NotebookSidebarType.Resources });
    this.track(AnalyticsEvent.Name.NotebookSidebarResourcesLinkClicked, 'click');
  }
  goToKeyboardShortcuts() {
    this.update({ tab: NotebookSidebarType.Help, items: { intro: false, shortcuts: true } });
    this.helpContainer.nativeElement.scrollTop = 0;
  }

  private update(params: Partial<PreferenceResource['notebook_sidebar']>) {
    params.items = params.items
      ? Object.assign(this.itemExpandedOrCollapsed, params.items)
      : this.itemExpandedOrCollapsed;
    params.tab = params.tab ?? this.activeTab;
    params.open = true;
    this.notebookFacade.updatePreference({ notebook_sidebar: params });
  }

  private track(event: AnalyticsEvent.Name, interaction_type: 'click' | 'impression', props = {}) {
    this.analytics.run(RunMethodType.Track, event, {
      product_context: AnalyticsEvent.Context.Notebook,
      product_subcontext: AnalyticsEvent.SubContext.Sidebar,
      interaction_type,
      notebook_token: this.notebookToken,
      notebook_kernel: this.notebookType,
      ...props,
    });
  }
}
